export type RadioStationId =
  | "m1"
  | "lietus"
  | "radiocentras"
  | "zipfm"
  | "relaxfm"
  | "rockfm";

export interface RadioStation {
  readonly id: RadioStationId;
  readonly label: string;
  readonly description: string;
  readonly streamUrl: string;
  readonly websiteUrl: string;
}

export interface RadioPreferences {
  readonly schemaVersion: 1;
  readonly stationId: RadioStationId | null;
  readonly volume: number;
}

export const RADIO_PREFERENCES_STORAGE_KEY = "vilnius.radio.v1";

/**
 * Station-owned HTTPS MP3 streams that Chrome can play without a streaming
 * library. The game links directly to each broadcaster and never bundles or
 * proxies station programming.
 */
export const RADIO_STATIONS: readonly Readonly<RadioStation>[] = Object.freeze([
  Object.freeze({
    id: "m1",
    label: "M-1",
    description: "Lithuanian pop radio",
    streamUrl: "https://stream.m-1.fm/m1/mp3",
    websiteUrl: "https://m-1.fm/",
  }),
  Object.freeze({
    id: "lietus",
    label: "Lietus",
    description: "Lithuanian songs",
    streamUrl: "https://stream.m-1.fm/lietus/mp3",
    websiteUrl: "https://lietus.15min.lt/",
  }),
  Object.freeze({
    id: "radiocentras",
    label: "Radiocentras",
    description: "Popular music and talk",
    streamUrl: "https://stream4.radijas.lt/rc",
    websiteUrl: "https://www.radijas.lt/radiocentras",
  }),
  Object.freeze({
    id: "zipfm",
    label: "ZIP FM",
    description: "Dance and electronic",
    streamUrl: "https://stream4.radijas.lt/zipfm",
    websiteUrl: "https://zipfm.lt/",
  }),
  Object.freeze({
    id: "relaxfm",
    label: "Relax FM",
    description: "Soft favourites",
    streamUrl: "https://stream4.radijas.lt/relaxfm",
    websiteUrl: "https://relaxfm.lt/",
  }),
  Object.freeze({
    id: "rockfm",
    label: "Rock FM",
    description: "Rock radio",
    streamUrl: "https://stream4.radijas.lt/rockfm",
    websiteUrl: "https://www.radijas.lt/rock-fm",
  }),
]);

export const DEFAULT_RADIO_PREFERENCES: Readonly<RadioPreferences> =
  Object.freeze({
    schemaVersion: 1,
    stationId: null,
    volume: 0.55,
  });

const STATIONS_BY_ID = new Map(
  RADIO_STATIONS.map((station) => [station.id, station] as const),
);

export function radioStationById(
  stationId: RadioStationId,
): Readonly<RadioStation> {
  const station = STATIONS_BY_ID.get(stationId);
  if (station === undefined) {
    throw new Error(`Unknown radio station ${stationId}.`);
  }
  return station;
}

function normalizedVolume(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return DEFAULT_RADIO_PREFERENCES.volume;
  }
  return Math.min(1, Math.max(0, value));
}

function normalizedStationId(value: unknown): RadioStationId | null {
  return typeof value === "string" && STATIONS_BY_ID.has(value as RadioStationId)
    ? value as RadioStationId
    : null;
}

export function normalizeRadioPreferences(value: unknown): RadioPreferences {
  const record = typeof value === "object" && value !== null
    ? value as Record<string, unknown>
    : {};
  if (
    record.schemaVersion !== undefined &&
    record.schemaVersion !== DEFAULT_RADIO_PREFERENCES.schemaVersion
  ) {
    return { ...DEFAULT_RADIO_PREFERENCES };
  }
  return {
    schemaVersion: 1,
    stationId: normalizedStationId(record.stationId),
    volume: normalizedVolume(record.volume),
  };
}

export function parseRadioPreferences(serialized: string): RadioPreferences {
  try {
    return normalizeRadioPreferences(JSON.parse(serialized));
  } catch {
    return { ...DEFAULT_RADIO_PREFERENCES };
  }
}

export function loadRadioPreferences(
  storage: Pick<Storage, "getItem">,
): RadioPreferences {
  try {
    const stored = storage.getItem(RADIO_PREFERENCES_STORAGE_KEY);
    return stored === null
      ? { ...DEFAULT_RADIO_PREFERENCES }
      : parseRadioPreferences(stored);
  } catch {
    return { ...DEFAULT_RADIO_PREFERENCES };
  }
}

export function persistRadioPreferences(
  storage: Pick<Storage, "setItem">,
  preferences: Readonly<RadioPreferences>,
): boolean {
  try {
    storage.setItem(
      RADIO_PREFERENCES_STORAGE_KEY,
      JSON.stringify(normalizeRadioPreferences(preferences)),
    );
    return true;
  } catch {
    return false;
  }
}
